import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DEBOUNCE_TIME } from 'data/global';
import { FilterField, FilterOption } from 'models/Filters';
import { getFilterParams } from 'components/MultiFilterModal/hooks/useMultiFilterForm';
import { Loader } from 'ncoded-component-library';
import { doesValueExist, getHasElementsInArray } from 'utils';

import './ResultsCount.styles.scss';

export type GetResultsCount = (
  filterParams: Record<string, unknown>,
) => Promise<number>;

type ResultsCountProps = {
  className?: string;
  filterValues: FilterField[];
  filterOptions: FilterOption[];
  getResultsCount: GetResultsCount;
};

const ResultsCount: React.FC<ResultsCountProps> = (props) => {
  const { className, filterValues, filterOptions, getResultsCount } = props;

  const [count, setCount] = useState<number>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();

  const filterParams = useMemo(
    () => getFilterParams(filterValues, filterOptions),
    [filterValues, filterOptions],
  );

  const filterParamsKey = JSON.stringify(filterParams);

  const hasFilters = getHasElementsInArray(Object.keys(filterParams));

  const resetState = () => {
    setCount(null);
    setIsLoading(false);
  };

  useEffect(() => {
    if (!hasFilters) {
      resetState();
      return;
    }

    let isCancelled = false;

    setIsLoading(true);

    const timeoutId = setTimeout(async () => {
      try {
        const totalItems = await getResultsCount(filterParams);

        if (!isCancelled) {
          setCount(totalItems);
          setIsLoading(false);
        }
      } catch (e) {
        if (!isCancelled) {
          resetState();
        }
      }
    }, DEBOUNCE_TIME);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFilters, filterParamsKey, getResultsCount]);

  return (
    <p className={classNames('filter-results-count', className)}>
      {isLoading ? (
        <span className="filter-results-count__loader">
          <Loader size="small" inline />
          {t('results')}.
        </span>
      ) : (
        doesValueExist(count) &&
        (count ? t('filterResultsCount', { count }) : t('noMatchesFound'))
      )}
    </p>
  );
};

export default ResultsCount;
